// Auto-save middleware — debounced PATCH to Supabase
// Groups changes by target (block or project) and flushes after DEBOUNCE_MS

import { saveBlock, saveProjectMeta, saveProjectMetaMergingSettings, isBlockSaveConflictError, type SaveBlockOptions } from '@/services/project-api'
import { getBlockBaseline } from '@/services/block-baselines'
import { ThumbnailService } from '@/services/thumbnail-service'

type SaveTarget =
  | { type: 'block'; blockId: string; field: string; data: unknown }
  | { type: 'project'; field: string; data: unknown }

interface PendingSave {
  key: string
  target: SaveTarget
  /** Project the edit belongs to, captured at QUEUE time. Resolving the
   * project at flush time routed edits queued under project A to whatever
   * project was current when the 500ms debounce fired — a cross-project
   * write on fast project switches, or a silent drop (reported as saved)
   * when the project was closed. */
  projectId: string | null
}

const pendingChanges = new Map<string, PendingSave>()
let saveTimer: ReturnType<typeof setTimeout> | null = null
const DEBOUNCE_MS = 500

/** Flushes currently in flight. flushSaves clears pendingChanges BEFORE
 * awaiting the network, so without this marker hasPendingChanges() reported
 * false exactly while an abortable fetch was in flight — a tab close in
 * that window dropped the PATCH invisibly (F27). */
let inFlightSaves = 0

/** Direct, client-coordinated multi-authority persistence that intentionally
 * bypasses the debounced queue. Keep this separate from `inFlightSaves`:
 * coordinated mutation handlers call `hasPendingChanges()` while holding the
 * dispatch lock to detect user edits, so folding this counter into that
 * predicate would make the lock report itself as a conflict. The dismissal
 * guard consumes this explicit counter instead. */
let coordinatedPersistenceInFlight = 0

/** F11 reload-fence epoch (L2-COR-04 / PI-L2-04 / CS-3 family). A conflict
 * reload re-seeds the project's blocks and OCC baselines wholesale; any
 * lane-1 dispatch that was ALREADY in flight when the reload began carries
 * a pre-reload payload + a now-defunct baseline. If such a straggler settles
 * AFTER the reload it must not leak into the reloaded world: a transient
 * failure must NOT re-queue its stale payload (a later flush would CAS-pass
 * it against the fresh baseline and silently stomp the reloaded server
 * truth), and a late 409 must NOT re-arm the conflict banner the reload just
 * cleared. dispatchEntries captures this epoch at dispatch time; the
 * settle-time loop drops both effects when the captured epoch is stale.
 * resolveSaveConflictByReload bumps it (via beginReloadFence) AFTER awaiting
 * waitForActiveFlush() — so the only stragglers it fences are ones that
 * outran the await, which is exactly the race window. */
let flushEpoch = 0

/** F11 — increment the reload-fence epoch, invalidating any in-flight lane-1
 * dispatch's settle-time effects (re-queue + markSaveConflict). Returns the
 * new epoch (unused by callers today; handy for tests/diagnostics). */
export function beginReloadFence(): number {
  flushEpoch += 1
  return flushEpoch
}

/** F11 — the single-flight chain promise, so callers (the conflict reload)
 * can fence against the current in-flight dispatch before re-seeding state.
 * Resolves when the current chain settles; never rejects (the latch already
 * swallows dispatch rejections). Re-read after awaiting if more work may
 * have chained on. */
export function waitForActiveFlush(): Promise<void> {
  return activeFlush
}

export interface FlushSavesOptions {
  /** Thread `fetch` keepalive into the PATCHes so a flush fired at page
   * dismissal (visibilitychange→hidden / beforeunload) can outlive the
   * document. Keepalive bodies share a ~64KB in-flight quota: an oversize
   * payload makes the fetch reject and re-enter the standard failure
   * re-queue below, which keeps hasPendingChanges() true and the dismissal
   * guard armed — that re-queue IS the oversize protection (no byte
   * thresholds here). Option bag on purpose: F11 extends it (baseline). */
  keepalive?: boolean
}

// Synchronous projectId source, registered by initAutoSaveBridge (which owns
// the studio store reference). A static studio-store import here would close
// the cycle studio-store → data-slice → auto-save, hence the indirection.
let projectIdSource: (() => string | null) | null = null

/** Register the synchronous current-project resolver (called once at startup). */
export function registerAutoSaveProjectIdSource(source: () => string | null): void {
  projectIdSource = source
}

/** Queue a change for debounced save */
export function queueSave(target: SaveTarget) {
  const key = target.type === 'block'
    ? `block:${target.blockId}:${target.field}`
    : `project:${target.field}`
  pendingChanges.set(key, { key, target, projectId: projectIdSource?.() ?? null })

  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(flushSaves, DEBOUNCE_MS)
}

/** Single-flight latch (L2-COR-02 family): every dispatch is chained behind
 * the previous one. Without serialization, a second flush could build its
 * jobs — and read its If-Match baselines — while the first PATCH was still
 * in flight: both legs then carried the SAME stale token, so under the CAS
 * contract the user's OWN pipelined edit self-409'd ("another session
 * modified this block" with no other session involved) and the typed
 * conflict branch deliberately dropped the NEWEST payload. Chaining moves
 * the pending-map snapshot AND the baseline reads to true dispatch time,
 * after the prior flush's response has refreshed the tokens. The latch
 * swallows rejections so a failed flush never poisons the chain (each
 * caller still observes ITS flush's rejection via the returned promise). */
let activeFlush: Promise<void> = Promise.resolve()

/** Flush all pending saves immediately.
 * Serialized: if a dispatch is already in flight, this flush's dispatch is
 * queued behind it and snapshots the queue (and the per-block baselines)
 * when IT actually runs. Each chained dispatch closes over its own caller's
 * options, so a keepalive dismissal flush queued behind a normal one still
 * keepalives its own PATCHes. */
export async function flushSaves(options?: FlushSavesOptions) {
  if (saveTimer) {
    clearTimeout(saveTimer)
    saveTimer = null
  }

  const run = activeFlush.then(async () => {
    const entries = Array.from(pendingChanges.values())
    pendingChanges.clear()
    if (entries.length === 0) return

    // In-flight bracket: incremented synchronously after the snapshot/clear
    // above (no await in between) and decremented only when every job has
    // settled AND failures have been re-queued — hasPendingChanges() never
    // flickers false while work is unsettled. (While this dispatch waits in
    // the chain, the entries are still IN pendingChanges, so the predicate
    // stays honest across the latch too.)
    inFlightSaves += 1
    try {
      await dispatchEntries(entries, options)
    } finally {
      inFlightSaves -= 1
    }
  })
  activeFlush = run.catch(() => {})
  return run
}

/**
 * Drain existing saves, then hold the dispatch chain while a coordinated
 * multi-authority commit performs its direct OCC writes. Saves queued by a
 * concurrent user edit remain pending behind the lock and can be detected by
 * the caller before it commits its local mirror.
 */
export async function withAutoSaveDispatchLock<T>(
  operation: () => Promise<T>,
): Promise<T> {
  await flushSaves()
  const prior = activeFlush
  let release!: () => void
  const gate = new Promise<void>((resolve) => {
    release = resolve
  })
  activeFlush = prior.then(() => gate).catch(() => {})
  await prior
  coordinatedPersistenceInFlight += 1
  try {
    return await operation()
  } finally {
    coordinatedPersistenceInFlight -= 1
    release()
  }
}

/** True for the complete direct-write and compensation window held by a
 * coordinated mutation. This keeps beforeunload armed even though those
 * writes are not represented in the debounced pending map. */
export function hasCoordinatedPersistenceInFlight(): boolean {
  return coordinatedPersistenceInFlight > 0
}

async function dispatchEntries(entries: PendingSave[], options?: FlushSavesOptions) {
  // F11 reload fence: snapshot the epoch at dispatch time. If a reload bumps
  // it while this dispatch's network is in flight, the settle-time re-queue
  // and conflict-arming below are dropped — the reload owns the post-reload
  // world and these payloads/baselines are pre-reload (stale).
  const dispatchEpoch = flushEpoch

  // Notify store that saving is in progress
  const { useStudioStore } = await import('./studio-store')
  const store = useStudioStore.getState()
  useStudioStore.setState({ isSaving: true } as any)

  // Entries queued before the provider registered fall back to the
  // flush-time project (the old behavior, now the exception).
  const fallbackProjectId = store.currentProjectId

  // Group by (projectId, target) so each PATCH goes to the project the
  // edit was made in — even if the user has since opened another project.
  const blockGroups = new Map<string, { projectId: string; blockId: string; updates: Record<string, unknown>; sources: PendingSave[] }>()
  const projectGroups = new Map<string, { projectId: string; updates: Record<string, unknown>; sources: PendingSave[] }>()
  let droppedCount = 0

  for (const entry of entries) {
    const projectId = entry.projectId ?? fallbackProjectId
    if (!projectId) {
      droppedCount += 1
      continue
    }
    if (entry.target.type === 'block') {
      const groupKey = `${projectId}:${entry.target.blockId}`
      const group = blockGroups.get(groupKey) ?? { projectId, blockId: entry.target.blockId, updates: {}, sources: [] }
      group.updates[entry.target.field] = entry.target.data
      group.sources.push(entry)
      blockGroups.set(groupKey, group)
    } else {
      const group = projectGroups.get(projectId) ?? { projectId, updates: {}, sources: [] }
      group.updates[entry.target.field] = entry.target.data
      group.sources.push(entry)
      projectGroups.set(projectId, group)
    }
  }

  if (droppedCount > 0) {
    console.warn(`[auto-save] Dropped ${droppedCount} change(s) with no resolvable project`)
  }

  // Plan B2 Task 5 — divergedAt boundary-exit marking. Every block save
  // dispatched from THIS queue is by construction a non-form edit
  // (`applySlotValues` persists its block writes directly via `api.saveBlock`,
  // never through `queueSave`/this debounce path), so this is exactly the
  // "editor/canvas edit" hook point the marking needs. A dynamic import here
  // (rather than a static one) is deliberate: `project-lineage.ts` imports
  // `studio-store` statically, and this module already avoids a static
  // studio-store import itself to dodge the studio-store -> data-slice ->
  // auto-save cycle (see the `projectIdSource` comment above) — importing
  // `project-lineage.ts` statically would just re-close that same cycle one
  // hop later. One marking attempt per distinct project id present in this
  // dispatch (almost always exactly one); the function's own guards (flag +
  // current-project match + already-diverged) make extra calls harmless.
  if (blockGroups.size > 0) {
    const { markTemplateDivergenceOnForeignEdit } = await import(
      '@/features/templates/content-form/project-lineage'
    )
    const markedProjectIds = new Set<string>()
    for (const group of blockGroups.values()) {
      if (markedProjectIds.has(group.projectId)) continue
      markedProjectIds.add(group.projectId)
      markTemplateDivergenceOnForeignEdit(group.projectId)
    }
  }

  // Execute saves in parallel, keeping each promise paired with its source
  // entries so failures can be re-queued instead of silently discarded.
  // The opts object is only passed when something is in it (keepalive from a
  // dismissal flush, baseline when this client holds an OCC token for the
  // block — F11) so the default call shape stays byte-identical.
  const projectSaveOpts = options?.keepalive ? { keepalive: true as const } : undefined
  const jobs: Array<{
    promise: Promise<unknown>
    sources: PendingSave[]
    /** Present for block jobs — lets the settle loop attribute a typed 409
     * to its block + edited fields (F11 conflict surface). */
    block?: { projectId: string; blockId: string; fields: string[] }
  }> = []
  for (const group of blockGroups.values()) {
    const baseline = getBlockBaseline(group.blockId)
    const saveOpts: SaveBlockOptions = {}
    if (baseline) saveOpts.baseline = baseline
    if (options?.keepalive) saveOpts.keepalive = true
    jobs.push({
      promise: Object.keys(saveOpts).length > 0
        ? saveBlock(group.projectId, group.blockId, group.updates, saveOpts)
        : saveBlock(group.projectId, group.blockId, group.updates),
      sources: group.sources,
      block: {
        projectId: group.projectId,
        blockId: group.blockId,
        fields: Object.keys(group.updates),
      },
    })
  }
  for (const group of projectGroups.values()) {
    // F2 — a queued `settings` payload is an OVERLAY of the keys its lane
    // owns (design-system state/tokens + plan), NOT the whole settings
    // object: the PATCH replaces the whole JSON column server-side, so
    // dispatch merges the overlay onto a FRESH read of the row's current
    // settings (saveProjectMetaMergingSettings documents why a load-time
    // store copy is not a correct base, and fails CLOSED on a failed read —
    // the rejection lands in the standard re-queue below). HONEST RESIDUAL:
    // on a keepalive dismissal flush the extra read leg can be aborted at
    // document teardown before the PATCH fires — accepted over the
    // whole-object sibling-key clobber this prevents (same residual class
    // as the ~64KB keepalive quota above).
    const mergesSettings = Object.prototype.hasOwnProperty.call(group.updates, 'settings')
    jobs.push({
      promise: mergesSettings
        ? saveProjectMetaMergingSettings(group.projectId, group.updates, projectSaveOpts)
        : projectSaveOpts
          ? saveProjectMeta(group.projectId, group.updates, projectSaveOpts)
          : saveProjectMeta(group.projectId, group.updates),
      sources: group.sources,
    })
  }

  if (jobs.length === 0) {
    // Nothing was saveable — do not report a clean save.
    useStudioStore.setState({ isSaving: false } as any)
    return
  }

  const results = await Promise.allSettled(jobs.map((job) => job.promise))

  // F11 reload fence: a reload that began after this dispatch started has
  // re-seeded the project's blocks + baselines. This dispatch's payloads are
  // pre-reload; dropping their settle-time effects (re-queue + conflict-arm)
  // is the whole point of the fence. isSaving is still cleared below so the
  // UI doesn't hang on a stale "saving" flag. We DON'T markSaved either — the
  // reload already owns the clean state.
  const reloadFenced = dispatchEpoch !== flushEpoch
  if (reloadFenced) {
    useStudioStore.setState({ isSaving: false } as any)
    return
  }

  const failed: unknown[] = []
  const failedKeys: string[] = []
  const conflictLegs: Array<{
    projectId: string
    blockId: string
    fields: string[]
    serverUpdatedAt: string | null
  }> = []
  results.forEach((result, index) => {
    if (result.status !== 'rejected') return
    const job = jobs[index]
    // F11 — typed-conflict branch FIRST, before the generic re-queue: a 409
    // means the server holds a NEWER revision; retrying the exact stale
    // payload IS the lost-update bug this lane fixes, so the payload is
    // deliberately NOT re-queued (the dismissal guard stays armed via the
    // saveConflict store state instead — see shouldBlockDismissal). The
    // baseline is NOT advanced either: only the user's reload resolves.
    // Ordering matters: a keepalive teardown flush that 409s must land here,
    // never in the failure re-queue below.
    if (job.block && isBlockSaveConflictError(result.reason)) {
      conflictLegs.push({
        projectId: job.block.projectId,
        blockId: job.block.blockId,
        fields: job.block.fields,
        serverUpdatedAt: result.reason.serverUpdatedAt,
      })
      return
    }
    failed.push(result.reason)
    // Re-queue the failed payloads (unless a newer edit superseded the key
    // while the save was in flight). No timer re-arm — retry happens on the
    // next edit or explicit Save/flush, avoiding a hot retry loop against a
    // persistently failing backend.
    for (const source of job.sources) {
      failedKeys.push(source.key)
      if (!pendingChanges.has(source.key)) {
        pendingChanges.set(source.key, source)
      }
    }
  })

  if (conflictLegs.length > 0) {
    // Named DISTINCTLY from the retryable failed legs below (PI-7 sibling):
    // these legs are halted, not pending — a multi-block fan-out (flow
    // rename/delete) that conflicts on a subset leaves the server torn until
    // the user reloads, and the leg names make the tear diagnosable.
    console.warn(
      `[auto-save] Conflict-halted save leg(s) (NOT re-queued): ${
        conflictLegs.map((c) => `block:${c.blockId}:${c.fields.join('+')}`).join(', ')
      }`,
    )
    store.markSaveConflict({
      projectId: conflictLegs[0].projectId,
      blocks: conflictLegs.map((c) => ({
        blockId: c.blockId,
        fields: c.fields,
        serverUpdatedAt: c.serverUpdatedAt,
      })),
    })
  }

  if (failed.length > 0) {
    // PI-7 — name the failed legs (block:<id>:<field> / project:<field>
    // keys): a multi-block semantic operation (flow rename/delete fans out
    // one PATCH per touched block) can land partially, leaving the server
    // torn until the re-queued legs retry; the per-leg keys make the tear
    // diagnosable. markSaveFailed below flips saveError, so the user-visible
    // save-failed surface fires for ANY failed leg.
    console.warn(`[auto-save] Failed save leg(s) re-queued: ${failedKeys.join(', ')}`)
    console.error('[auto-save] Some saves failed:', failed)
    const firstReason = failed[0]
    const message = firstReason instanceof Error
      ? firstReason.message
      : typeof firstReason === 'object' && firstReason !== null && 'message' in firstReason
        ? String((firstReason as { message?: unknown }).message)
        : 'Auto-save failed'
    store.markSaveFailed(`Auto-save failed: ${message}`)
    return
  }

  // A conflicted edit never landed — do not report a clean save (markSaved
  // would flip isDirty/saveError as if everything durably persisted).
  if (conflictLegs.length > 0) return

  // Mark as saved
  store.markSaved()

  // Schedule deferred thumbnail refresh after successful saves.
  // A slug is no longer required: the draft producer captures the studio's own
  // render and needs no live subdomain. `isPublished` is what decides whether
  // the platform's `https://{slug}.cogo.xyz` screenshot is even a legal
  // fallback — for a draft that URL 404s.
  if (store.currentProjectId) {
    const meta = (store as { projectMeta?: { slug?: string | null; status?: string } }).projectMeta
    ThumbnailService.scheduleRefresh(store.currentProjectId, meta?.slug ?? '', {
      isPublished: meta?.status === 'published' && Boolean(meta?.slug),
    })
  }
}

/** Check if there are unsaved changes — true while edits sit in the
 * debounce queue, while failed payloads wait re-queued, AND while a flush
 * is in flight (the map is cleared before the network await, so the
 * in-flight marker is what keeps the dismissal guard honest mid-PATCH). */
export function hasPendingChanges(): boolean {
  return pendingChanges.size > 0 || inFlightSaves > 0
}

/** F11 conflict-resolution support: drop every queued (not in-flight) save
 * belonging to a project before reloading it — the reload replaces the very
 * state those payloads were derived from, so flushing them afterwards would
 * stomp the freshly loaded server truth. Entries queued before the projectId
 * provider registered (projectId null) resolve to the CURRENT project at
 * flush time; at conflict-resolution time that is the reloading project, so
 * they are dropped too. */
export function discardPendingSavesForProject(projectId: string): void {
  for (const [key, entry] of pendingChanges) {
    if (entry.projectId === projectId || entry.projectId === null) {
      pendingChanges.delete(key)
    }
  }
}

// ---------------------------------------------------------------------------
// Flush pending thumbnail generation on page unload / logout
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    ThumbnailService.flush()
  })
}
